<script>
    import { isAuthenticated, hostname, stack, accessInfo } from "../stores.js";
    import { now, sleep, isNumeric, listSessionsApi, listAllOngoingPipelinesApi, listPipelinesStatusApi } from '../utils.js';
    import { toast } from "@zerodevx/svelte-toast";
    import {  toastThemeError, toastThemeErrorDismissable, toastThemeSuccessDismissable } from "../toasts.js";
    import { waitingOrRunningPipelineIds, pipelineObserver, allPipelines, availableSessions, sessionsPageSize, sessionsSkip, availableSessionsTotalCount, isSessionsListLegacy, isSessionsListLoading, isPipelinesListLoading, sessionFilter } from "../pipelinestores.js";
    import { onMount } from "svelte";

    import * as Sentry from '@sentry/browser';

    export const events = new EventTarget();

    $: {
        if ($isAuthenticated && $stack && $accessInfo) {
            console.log("~~~~~~~~~~~~~~~INITIALISING PIPELINE OBSERVER BECAUSE AUTHENTICATED~~~~~~~~~~~~~~~~");
            initialiseWithData();
        } else {
            console.log('hostname=', $hostname, ', isAuthenticated=', $isAuthenticated, ', $stacks=', $stack);
        }
    }

    // Singleton instance
    let pipelineObserverComponent;
    export function getPipelineObserverComponent() {
        if (!pipelineObserverComponent) {
            pipelineObserverComponent = this;
            $pipelineObserver = pipelineObserverComponent;
        }
        return pipelineObserverComponent;
    }

    // Helper utility method to compare two arrays for equality (regardless of order)
    const compareArrays = (a, b) =>
        a.length === b.length &&
        a.every((element, index) => b.includes(element));

    // DEBUG: Set UPDATE_DISABLED to true if you want to turn off pipeline checks temporarily
    let UPDATE_DISABLED = false;

    // Periodically fetch list of pipelines for all sessions.
    // Warning: This will trigger an API call for every updating page.
    export async function addPipelineListener() {
        var singletonIsListening = false;
        if (singletonIsListening == true) return;

        const MAX_FETCH_ERRORS = 5;
        let errorCount = 0;

        while ($waitingOrRunningPipelineIds?.length > 0 && errorCount <= MAX_FETCH_ERRORS && !UPDATE_DISABLED) {
            singletonIsListening = true;
            await sleep(60000);
            console.log('LISTENING TO PIPELINES (WITH DUPLICATES):', $waitingOrRunningPipelineIds);
            const pipelineIdStatuses = await listPipelinesStatusApi($hostname, $isAuthenticated, $stack, $waitingOrRunningPipelineIds);

            // DEBUG: Sometimes pipeline statuses fails to return status array.
            // Log returned object to try and find out why.
            if (!Array.isArray(pipelineIdStatuses)) {
                errorCount++;
                console.error(`ERROR #${errorCount} FETCHING PIPELINE STATUS: ${pipelineIdStatuses}`);
                try {
                    throw new Error(`Sentry ERROR #${errorCount} FETCHING PIPELINE STATUS: ${pipelineIdStatuses}`);
                } catch (err) {
                    Sentry.captureException(err);
                }
            } else {
                if (errorCount > 0) {
                    console.log(`ERROR COUNT FETCHING PIPELINE STATUS ${errorCount} RESET TO 0 BECAUSE WE GOT DATA!`);
                    errorCount = 0; // Fetch started working again
                }
                
                let updatedWaitingOrRunningPipelineStatuses = pipelineIdStatuses?.filter(p => {return p.status == 'Waiting' || p.status == 'Running' || p.status == 'Unscheduled'});
                console.log('UPDATED WAITINGORRUNNINGPIPELINE STATUSES: ', updatedWaitingOrRunningPipelineStatuses);
                if (updatedWaitingOrRunningPipelineStatuses?.length < $waitingOrRunningPipelineIds?.length) {
                    // Some change has happened, update the observables list
                    $waitingOrRunningPipelineIds = updatedWaitingOrRunningPipelineStatuses?.map(x => x.pipeline_id);

                    // Extract failed and done pipelines to be handled differently (e.g. notifications)
                    const failedPipelineIdStatuses = pipelineIdStatuses?.filter(p => {return p.status == 'Failed'});
                    const donePipelineIdStatuses = pipelineIdStatuses?.filter(p => {return p.status == 'Done'});

                    let msg = `${now()}<br/>`;
                    if (failedPipelineIdStatuses?.length > 0) {
                        if(errorCount <= 1) {
                            msg += `Pipelines failed: ${failedPipelineIdStatuses?.map(x => x.pipeline_id)}`;
                            toast.push(msg, toastThemeErrorDismissable);
                        }
                        // Send event so that other components can do whatever updates they want when a pipeline has failed
                        const event = new CustomEvent('pipelinesfailed', {
                            detail: failedPipelineIdStatuses,
                            bubbles: true,
                            cancelable: true,
                            composed: true // makes the event jump shadow DOM boundary
                        });
                        events.dispatchEvent(event);
                        // OK, update the list of pipelines
                        refreshPipelines();
                    } else if (donePipelineIdStatuses?.length > 0) {
                        msg += `Pipelines done: ${donePipelineIdStatuses?.map(x => x.pipeline_id)}`;
                        toast.push(msg, toastThemeSuccessDismissable);
                        // Send event so that other components can do whatever updates they want when a pipeline is done
                        let event = new CustomEvent('pipelinesdone', {
                            detail: donePipelineIdStatuses,
                            bubbles: true,
                            cancelable: true,
                            composed: true // makes the event jump shadow DOM boundary
                        });
                        events.dispatchEvent(event);
                        // OK, update the list of pipelines
                        refreshPipelines();
                    }
                }
            }

            // If we are no longer observing any pipelines, stop listening/polling
            if ($waitingOrRunningPipelineIds?.length < 1 || errorCount > MAX_FETCH_ERRORS) {

                // Explain to the user tha we stop listening to status updates because of (fetch) errors
                if (errorCount > MAX_FETCH_ERRORS) {
                    let msg = `${now()}<br/>`;
                    msg += `Too many errors when fetching pipeline status updates. It may be a temporary issue. `;
                    msg += `Giving up listening for now. Refresh page to resume listening.`;
                    toast.push(msg, toastThemeErrorDismissable);
                }

                singletonIsListening = false;
                console.log('STOPPED LISTENING TO PIPELINES... Waiting: ', $waitingOrRunningPipelineIds);
            }
        }
    }

    export function addPipelineToObserve(pipelineId) {
        if ($waitingOrRunningPipelineIds?.includes(pipelineId) || !pipelineId || pipelineId == '') return;

        console.log('PIPELINE OBSERVER ADDED ', pipelineId, ' TO OBSERVABLES!');
        $waitingOrRunningPipelineIds?.push(pipelineId);
        //events.dispatchEvent(new CustomEvent('pipelineschanged', {detail: $waitingOrRunningPipelineIds}));
        
        addPipelineListener();
    }

    export function addPipelinesToObserve(pipelineIds) {
        let addedAtLeastOnePipeline = false;
        pipelineIds.forEach(pipelineId => {
            if ($waitingOrRunningPipelineIds?.includes(pipelineId) || !pipelineId || pipelineId == '') return;

            console.log('PIPELINE OBSERVER ADDED ', pipelineId, ' TO OBSERVABLES!');
            addedAtLeastOnePipeline = true;
            $waitingOrRunningPipelineIds?.push(pipelineId);
        });

        if (addedAtLeastOnePipeline) {
            $waitingOrRunningPipelineIds = $waitingOrRunningPipelineIds; // Make Svelte trigger reactive variable change
            //events.dispatchEvent(new CustomEvent('pipelineschanged', {detail: $waitingOrRunningPipelineIds}));

            addPipelineListener();
        }
    }

    /**
     * To be able to find out from scratch which pipelines to observe, we have to
     * 1. fetch the list of all sessions that the system knows about
     * 2. compile a list of all known pipelines (given by each session)
     * 3. fetch the list of pipelines status
     * 4. filter away all done and failed pipelines
     * 5. compile a list of all waiting or running pipelines
     */
    async function initialiseWithData() {

        console.log(`HOSTNAME=${$hostname}, ISAUTHENTICATED=${$isAuthenticated}, STACK=${$stack}`);
        console.log('ACCESSINFO=', $accessInfo);

        // If we don't have access information, do nothing
        //if (!$hostname || !$isAuthenticated || $stack) return;

        console.log("INITIALISING PIPELINE OBSERVER WITH DATA FROM STACK: ", $stack);

        // Upon initialisation, reset the list page
        $sessionsSkip = 0;
        $availableSessionsTotalCount = 0;

        // If we are already waiting for sessions response, do nothing
        if ($isSessionsListLoading) return;

        // Default behaviour is to list all sessions for all teams to which the user belongs
        const filter = getFilter();
        const skip = $sessionsSkip;
        const limit = !$isSessionsListLegacy ? $sessionsPageSize : 500; // Legacy support (no paging) can be removed once all backends are updated

        // Fetch list of all known sessions
        $isSessionsListLoading = true;
        const sessionApiResponse = await listSessionsApi($hostname, $isAuthenticated, $stack, filter, skip, limit);
        $isSessionsListLoading = false;
        if (isNumeric(sessionApiResponse)) {
            let errorMsg;
            switch (sessionApiResponse) {
                case 401:
                    errorMsg = "Not logged in";
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = sessionApiResponse;
            }
            let msg = `${now()}<br/>Sessions API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);
        } else {
            //DEBUG: let msg = `${now()}<br/>Pipeline observer listed ${sessionApiResponse?.length} sessions OK`;
            //DEBUG: toast.push(msg, toastThemeSuccess);
            console.log("ALL SESSIONS", $availableSessions);
            if (sessionApiResponse?.sessions) {
                $availableSessions = sessionApiResponse?.sessions;
                $availableSessionsTotalCount = sessionApiResponse?.total_count;
            } else if (Array.isArray(sessionApiResponse)) {
                // Legacy compatibility supporting plain sessions list in response
                // Can be removed when all backends are up-tp-date
                $isSessionsListLegacy = true;
                $availableSessions = sessionApiResponse.reverse();
                $availableSessionsTotalCount = sessionApiResponse.length;
            }
        }

        // If we are already waiting for ongoing sessions response, do nothing
        if ($isPipelinesListLoading) return;

        console.log("PIPELINE OBSERVER INITIALISING SESSIONS WITH DATA FROM STACK: ", $stack);
        console.log("... AND FILTER: ", filter);

        // Fetch all known pipelines
        $isPipelinesListLoading = true;

        const age = 3 * 24; // Age in hours of any pipeline with last modified time
        let pipelinesApiResponse = await listAllOngoingPipelinesApi(
            $hostname,
            $isAuthenticated,
            $stack,
            filter,
            age
        );
        $isPipelinesListLoading = false;
        if (isNumeric(pipelinesApiResponse)) {
            let errorMsg;
            switch (pipelinesApiResponse) {
                case 401:
                    errorMsg = "Not logged in";
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = pipelinesApiResponse;
            }
            let msg = `${now()}<br/>Pipelines API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);
        } else {
            //DEBUG: let msg = `${now()}<br/>Pipeline observer listed ${pipelinesApiResponse?.length} pipelines OK`;
            //DEBUG: toast.push(msg, toastThemeSuccess);

            // Clean out well-known error message objects from array before setting allPipelines
            if (Array.isArray(pipelinesApiResponse)) {
                pipelinesApiResponse = pipelinesApiResponse?.filter( p => !p.detail?.toLowerCase()?.includes("not found"));
                $allPipelines = pipelinesApiResponse || []; // Remember full list of pipelines from server

                //filterObservablePipelines(pipelinesApiResponse);
                let waitingOrRunningPipelines = $allPipelines?.filter(p => {return (p.status == 'Waiting' || p.status == 'Running' || p.status == 'Unscheduled')});
                $waitingOrRunningPipelineIds = waitingOrRunningPipelines?.map((x) => x.pipeline_id);
                console.log('PIPELINE OBSERVER HAS LIST OF WAITING OR RUNNING PIPELINES:', $waitingOrRunningPipelineIds);
                addPipelineListener();
            } else {
                $allPipelines = [];
            }
        }
    }

    const cutoffAgeSeconds = 24 * 3600 * 3;
    function filterObservablePipelines(allPipelines) {
        try {
            // Filter away old pipelines
            let pipelines = allPipelines?.filter((p) => {
                const date = new Date(p.start_time).getTime();
                const diff = Date.now() - date;
                const diffSeconds = diff / 1000;
                const secondsBetweenDates = Math.abs(diffSeconds);
                if (secondsBetweenDates > cutoffAgeSeconds) {
                    return false; // Discard old (2 days) pipelines
                }
                return true;
            });

            // Sort remaining pipelines by date
            pipelines?.sort((a, b) => {
                const dateA = new Date(a.start_time).getTime();
                const dateB = new Date(b.start_time).getTime();
                return dateA > dateB ? -1 : 1; // Descending order
            });
            console.log("Pipeline observer has pipelines object sorted = ", pipelines);
            //activePipelines = [...pipelines];
            let waitingOrRunningPipelines = pipelines?.filter(p => {return (p.status == 'Waiting' || p.status == 'Running' || p.status == 'Unscheduled')});
            waitingOrRunningPipelines = waitingOrRunningPipelines?.filter((p) => {
                const date = new Date(p.start_time).getTime();
                const diff = Date.now() - date;
                const diffSeconds = diff / 1000;
                const secondsBetweenDates = Math.abs(diffSeconds);
                if (secondsBetweenDates > cutoffAgeSeconds) {
                    return false; // Discard old (2 days) pipelines
                }
                return true;
            });
            $waitingOrRunningPipelineIds = waitingOrRunningPipelines?.map((x) => x.pipeline_id);
            console.log('PIPELINE OBSERVER HAS LIST OF WAITING OR RUNNING PIPELINES:', $waitingOrRunningPipelineIds);
            addPipelineListener();

        } catch (error) {
            console.log("PARSE PIPELINES ERROR:", error.message);
            //activePipelines = [];
            $waitingOrRunningPipelineIds = [];
        }
    }

    export async function refreshPipelines() {
        if ($isPipelinesListLoading) return; // Safeguard, allow only zero or one refresh running at any one time
        const filter = getFilter();

        console.log("PIPELINE OBSERVER REFRESHING PIPELINES WITH DATA FROM STACK: ", $stack);
        console.log("... AND FILTER: ", filter);

        $isPipelinesListLoading = true;

        const age = 3 * 24; // Age in hours of any pipeline with last modified time
        let pipelinesApiResponse = await listAllOngoingPipelinesApi(
            $hostname,
            $isAuthenticated,
            $stack,
            filter,
            age
        );
        $isPipelinesListLoading = false;
        if (isNumeric(pipelinesApiResponse)) {
            let errorMsg;
            switch (pipelinesApiResponse) {
                case 401:
                    errorMsg = "Not logged in";
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = pipelinesApiResponse;
            }
            let msg = `${now()}<br/>Pipelines API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);
            console.error(`Pipelines API error: ${errorMsg}`);
        } else {
            console.log('PIPELINE OBSERVER GOT THESE PIPELINES: ', pipelinesApiResponse);
            //DEBUG: let msg = `${now()}<br/>Listed ${pipelinesApiResponse?.length} pipelines OK`;
            //DEBUG: toast.push(msg, toastThemeSuccess);

            // Clean out well-known error message objects from array before setting allPipelines
            if (Array.isArray(pipelinesApiResponse)) {
                pipelinesApiResponse = pipelinesApiResponse?.filter( p => !p.detail?.toLowerCase()?.includes("not found"));
                $allPipelines = pipelinesApiResponse || [];

                //filterObservablePipelines(pipelinesApiResponse);
                let waitingOrRunningPipelines = $allPipelines?.filter(p => {return (p.status == 'Waiting' || p.status == 'Running' || p.status == 'Unscheduled')});
                $waitingOrRunningPipelineIds = waitingOrRunningPipelines?.map((x) => x.pipeline_id);
                console.log('PIPELINE OBSERVER HAS LIST OF WAITING OR RUNNING PIPELINES:', $waitingOrRunningPipelineIds);
                addPipelineListener();
            } else {
                $allPipelines = [];
            }
        }
    }

    export async function refreshSessions() {
        // Default behaviour is to list all sessions for all teams to which the user belongs
        const filter = getFilter();
        $isSessionsListLoading = true;
        console.log("PIPELINE OBSERVER REFRESHING SESSIONS WITH DATA FROM STACK: ", $stack);
        console.log("... AND FILTER: ", filter);
        const skip = $sessionsSkip;
        const limit = $sessionsPageSize;
        console.log("... AND SKIP: ", skip, " ... AND LIMIT: ", limit);
        const sessionApiResponse = await listSessionsApi($hostname, $isAuthenticated, $stack, filter, skip, limit);
        $isSessionsListLoading = false;
        if (isNumeric(sessionApiResponse)) {
            let errorMsg;
            switch (sessionApiResponse) {
                case 401:
                    errorMsg = "Not logged in";
                    break;
                case 503:
                    errorMsg = "Service unavailable";
                    break;
                default:
                    errorMsg = sessionApiResponse;
            }
            let msg = `${now()}<br/>Sessions API error: ${errorMsg}`;
            toast.push(msg, toastThemeError);
            throw new Error(`Sessions API error: ${errorMsg}`);
        } else {
            //DEBUG: let msg = `${now()}<br/>Listed sessions OK`;
            //DEBUG: toast.push(msg, toastThemeSuccess);
            if (sessionApiResponse?.sessions) {
                $availableSessions = sessionApiResponse?.sessions;
                $availableSessionsTotalCount = sessionApiResponse?.total_count;
            } else if (Array.isArray(sessionApiResponse)) {
                // Legacy compatibility supporting plain sessions list in response
                // Can be removed when all backends are up-tp-date
                $isSessionsListLegacy = true;
                $availableSessions = sessionApiResponse.reverse();
                $availableSessionsTotalCount = sessionApiResponse.length;
            }
        }
    }


    function getFilter() {
        let filter = {
            userIds: [],
            teamIds: [],
            sortOrder: ""
        };

        //If the team filter is empty, we fill the $sessionFilter with teamIds instead of sending an empty teamId, that is only for the admin
        //First we find the organisation/s that have the selected stack => then check if regular user && no id selected
        //Go throght all the organization with correct stack and add those teams to the request

        const stackOrganizations = $accessInfo?.userservice_org_teams.filter(org => org.stacks.some(s => s.name == $stack));
        if (stackOrganizations?.some(org => org.role !== "admin" && $sessionFilter.teamIds.length === 0)) {
            stackOrganizations.forEach(org => org.teams.forEach(team => filter.teamIds.push(team.id)));
        }
        else {
            filter.teamIds = $sessionFilter.teamIds;
        }
        filter.userIds = $sessionFilter.userIds;
        filter.sortOrder = $sessionFilter.sortOrder;
        return filter;
    }

    onMount(async () => {
        console.log('PIPELINEOBSERVER MOUNTED!!');
    });
</script>

<style>

</style>